"use client";

import { Button } from "@/components/ui/button";
import { createClient } from "@/lib/supabase/client";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import GoogleAuthLoadingScreen from "./GoogleAuthLoadingScreen";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/mixpanel-client";

interface GoogleAuthButtonProps {
  mode: "signin" | "signup";
  className?: string;
  pendingUploadId?: string;
}

export function GoogleAuthButton({
  mode,
  className,
  pendingUploadId,
}: GoogleAuthButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const supabase = createClient();
  const { toast } = useToast();
  const t = useTranslations("Auth");

  const handleGoogleAuth = async () => {
    try {
      setIsLoading(true);
      setShowLoadingScreen(true);

      // Store upload context if it exists
      if (pendingUploadId) {
        // Store only in sessionStorage
        sessionStorage.setItem("pendingAuthUpload", pendingUploadId);

        console.log(
          "Stored pending upload ID for post-auth processing:",
          pendingUploadId
        );
      }

      // Always redirect to dashboard - we'll handle the upload there
      const redirectTo = "https://taberu.ai/dashboard?pendingUpload=true";
      console.log("Google Auth redirectTo:", redirectTo);

      // Track auth attempt
      if (mode === "signup") {
        trackEvent("Signup Initiated", {
          Source: "Google Auth Button",
          Method: "Google",
        });
      } else {
        trackEvent("Login Initiated", {
          Method: "Google",
        });
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectTo,
          queryParams: {
            access_type: "offline",
          },
        },
      });

      if (error) {
        setShowLoadingScreen(false);
        throw error;
      }
    } catch (error) {
      console.error("Google auth error:", error);
      // Clean up stored upload ID on error
      if (pendingUploadId) {
        sessionStorage.removeItem("pendingAuthUpload");
      }
      setShowLoadingScreen(false);
      toast({
        variant: "destructive",
        title: t("error"),
        description: error instanceof Error ? error.message : t("authError"),
      });
    }
  };

  return (
    <>
      {showLoadingScreen && <GoogleAuthLoadingScreen />}
      <Button
        type="button"
        variant="outline"
        onClick={handleGoogleAuth}
        disabled={isLoading || showLoadingScreen}
        className={`${className} flex items-center justify-center gap-2 border-2 border-zinc-200 dark:border-zinc-800 hover:bg-zinc-100 dark:hover:bg-zinc-800`}
      >
        {isLoading || showLoadingScreen ? (
          t("loading")
        ) : (
          <>
            <svg
              className="h-5 w-5"
              aria-hidden="true"
              focusable="false"
              data-prefix="fab"
              data-icon="google"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 488 512"
            >
              <path
                fill="currentColor"
                d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"
              ></path>
            </svg>
            <span className="font-medium">
              {mode === "signin"
                ? t("continueWithGoogle")
                : mode === "signup"
                ? t("signUpWithGoogle")
                : t("continueWithGoogle")}
            </span>
          </>
        )}
      </Button>
    </>
  );
}
